import { useState } from "react";
import { classNames } from "primereact/utils";
import { format, addMonths, subMonths } from "date-fns";
import { ChevronLeftIcon, ChevronRightIcon } from "@heroicons/react/20/solid";

import { getEventChipClassName } from "../../utils/calendar/eventStatusStyles";

export default function MonthCalendar(props) {
  const {
    calendar,
    currentMonth,
    onChangeMonth,
    onDayClick,
    onEventClick,
    loading,
    createHref,
    createLabel,
    emptyMessage = "No events on this day.",
  } = props;

  const [expandedDay, setExpandedDay] = useState(null);

  const handleDayClick = (day) => {
    onDayClick(day);
  };

  const onMoreClick = (e, day) => {
    e.stopPropagation();
    setExpandedDay(day.date);
    handleDayClick(day);
  };

  const onShowLessClick = (e, day) => {
    e.stopPropagation();
    setExpandedDay((prev) => (prev === day.date ? null : prev));
  };

  const selectedDay =
    calendar.find((day) => day.isSelected) ??
    calendar.find((day) => day.isToday) ??
    null;

  return (
    <div className="px-4 lg:flex lg:h-full lg:flex-col">
      <header className="flex items-center justify-between border-b border-gray-200 px-6 py-4 lg:flex-none">
        <h1 className="text-base font-semibold text-gray-900">
          {format(currentMonth, "MMMM yyyy")}
        </h1>
        <div className="flex items-center">
          <div className="relative flex items-center rounded-md bg-white shadow-xs md:items-stretch">
            <button
              type="button"
              className="flex h-9 w-12 items-center justify-center rounded-l-md border-y border-l border-gray-300 pr-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pr-0 md:hover:bg-gray-50"
              onClick={() => onChangeMonth(subMonths(currentMonth, 1))}
              disabled={loading}
            >
              <span className="sr-only">Previous month</span>
              <ChevronLeftIcon className="size-5" aria-hidden="true" />
            </button>
            <button
              type="button"
              className="hidden border-y border-gray-300 px-3.5 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus:relative md:block"
              onClick={() => onChangeMonth(new Date())}
              disabled={loading}
            >
              {format(currentMonth, "MMMM yyyy")}
            </button>
            <span className="relative -mx-px h-5 w-px bg-gray-300 md:hidden" />
            <button
              type="button"
              className="flex h-9 w-12 items-center justify-center rounded-r-md border-y border-r border-gray-300 pl-1 text-gray-400 hover:text-gray-500 focus:relative md:w-9 md:pl-0 md:hover:bg-gray-50"
              onClick={() => onChangeMonth(addMonths(currentMonth, 1))}
              disabled={loading}
            >
              <span className="sr-only">Next month</span>
              <ChevronRightIcon className="size-5" aria-hidden="true" />
            </button>
          </div>
          <button
            type="button"
            className="ml-4 hidden h-9 rounded border border-gray-300 px-3.5 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus:relative md:block"
            onClick={() => onChangeMonth(new Date())}
            disabled={loading}
          >
            Today
          </button>
          {createHref && createLabel ? (
            <div className="hidden md:ml-4 md:flex md:items-center">
              <a
                href={createHref}
                className="ml-6 rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-xs hover:bg-indigo-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
              >
                {createLabel}
              </a>
            </div>
          ) : null}
        </div>
      </header>

      {loading ? (
        <div className="flex items-center justify-center py-16 text-sm text-gray-500">
          Loading calendar…
        </div>
      ) : (
        <div className="shadow-sm ring-1 ring-black/5">
          <div className="grid grid-cols-7 gap-px border-b border-gray-300 bg-gray-200 text-center text-xs/6 font-semibold text-gray-700 lg:flex-none">
            {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map((label) => (
              <div key={label} className="bg-white py-2">
                <span className="sm:hidden">{label.charAt(0)}</span>
                <span className="hidden sm:inline">{label}</span>
              </div>
            ))}
          </div>

          <div className="bg-gray-200 text-xs/6 text-gray-700">
            <div className="hidden w-full lg:grid lg:grid-cols-7 lg:gap-px lg:[grid-template-rows:repeat(6,minmax(4.5rem,max-content))]">
              {calendar.map((day) => (
                <div
                  key={day.date}
                  className={classNames(
                    day.isCurrentMonth
                      ? "bg-white"
                      : "bg-gray-50 text-gray-500",
                    "relative min-h-[4.5rem] cursor-pointer px-1 py-1.5",
                  )}
                  onClick={() => handleDayClick(day)}
                >
                  <time
                    dateTime={day.date}
                    className={
                      day.isToday
                        ? "flex size-6 items-center justify-center rounded-full bg-indigo-600 font-semibold text-white"
                        : day.isSelected && !day.isToday
                          ? "flex size-6 items-center justify-center rounded-full bg-gray-600 font-semibold text-white"
                          : undefined
                    }
                  >
                    {day.date.split("-").pop().replace(/^0/, "")}
                  </time>

                  {day.events?.length > 0 && (
                    <ol className="mt-1 space-y-1">
                      {(expandedDay === day.date
                        ? day.events
                        : day.events.slice(0, 2)
                      ).map((event) => (
                        <li
                          key={`${event.type}-${event.id}`}
                          className={classNames(
                            getEventChipClassName(event),
                            "rounded border px-1 py-px",
                          )}
                        >
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              onEventClick(event);
                            }}
                            className="group block w-full min-w-0 truncate text-start text-[11px] leading-tight font-bold group-hover:text-indigo-600"
                          >
                            {event.title}
                          </button>
                        </li>
                      ))}

                      {day.events.length > 2 && expandedDay !== day.date && (
                        <li className="text-gray-500">
                          <button
                            type="button"
                            onClick={(e) => onMoreClick(e, day)}
                            className="underline hover:text-gray-700"
                          >
                            + {day.events.length - 2} more
                          </button>
                        </li>
                      )}

                      {day.events.length > 2 && expandedDay === day.date && (
                        <li className="text-gray-500">
                          <button
                            type="button"
                            onClick={(e) => onShowLessClick(e, day)}
                            className="underline hover:text-gray-700"
                          >
                            Show less
                          </button>
                        </li>
                      )}
                    </ol>
                  )}
                </div>
              ))}
            </div>

            <div className="isolate grid w-full grid-cols-7 grid-rows-6 gap-px lg:hidden">
              {calendar.map((day) => (
                <button
                  key={day.date}
                  type="button"
                  className={classNames(
                    day.isCurrentMonth ? "bg-white" : "bg-gray-50",
                    (day.isSelected || day.isToday) && "font-semibold",
                    day.isSelected && "text-white",
                    !day.isSelected && day.isToday && "text-indigo-600",
                    !day.isSelected &&
                      day.isCurrentMonth &&
                      !day.isToday &&
                      "text-gray-900",
                    !day.isSelected &&
                      !day.isCurrentMonth &&
                      !day.isToday &&
                      "text-gray-500",
                    "flex h-14 flex-col px-3 py-2 hover:bg-gray-100 focus:z-10",
                  )}
                  onClick={() => handleDayClick(day)}
                >
                  <time
                    dateTime={day.date}
                    className={classNames(
                      day.isSelected &&
                        "flex size-6 items-center justify-center rounded-full",
                      day.isSelected && day.isToday && "bg-indigo-600",
                      day.isSelected && !day.isToday && "bg-gray-500",
                      "ml-auto",
                    )}
                  >
                    {day.date.split("-").pop().replace(/^0/, "")}
                  </time>
                  <span className="sr-only">
                    {day.events?.length ?? 0} events
                  </span>
                  {day.events?.length > 0 && (
                    <span className="-mx-0.5 mt-auto flex flex-wrap-reverse">
                      {day.events.map((event) => (
                        <span
                          key={`${event.type}-${event.id}`}
                          className="mx-0.5 mb-1 size-1.5 rounded-full bg-gray-400"
                        />
                      ))}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}

      {selectedDay && (
        <div className="px-4 py-10 sm:px-6">
          {selectedDay.events?.length > 0 ? (
            <ol className="divide-y divide-gray-100 overflow-hidden rounded-lg bg-white text-sm shadow-sm ring-1 ring-black/5">
              {selectedDay.events.map((event) => (
                <li
                  key={`${event.type}-${event.id}`}
                  className="group flex p-4 pr-6 focus-within:bg-gray-50 hover:bg-gray-50"
                >
                  <div className="flex-auto">
                    <p className="font-semibold text-gray-900">{event.title}</p>
                    <p className="mt-1 text-xs text-gray-500">
                      {event.status?.label}
                      {event.is_overdue ? " · Overdue" : ""}
                    </p>
                  </div>
                  <button
                    type="button"
                    onClick={() => onEventClick(event)}
                    className="ml-6 flex-none self-center rounded-md bg-white px-3 py-2 font-semibold text-gray-900 opacity-0 shadow-xs ring-1 ring-gray-300 ring-inset group-hover:opacity-100 hover:ring-gray-400 focus:opacity-100"
                  >
                    View
                  </button>
                </li>
              ))}
            </ol>
          ) : (
            <p className="text-center text-sm text-gray-500">{emptyMessage}</p>
          )}
        </div>
      )}
    </div>
  );
}
